import {
  THEMES,
  FREE_THEMES,
  SUPPORTER_THEMES,
  BOARD_SETS,
  BOARD_SET_IDS,
  type ThemeId,
  type BoardSetId,
} from '@ccc/design-tokens';
import { useThemeStore, PIECE_SETS, type PieceSetMeta } from '../lib/theme';
import { InlinePiece, isInlineSet, type InlinePieceSetId } from './InlinePieces';
import { cn } from '../lib/classnames';

interface ThemeSwitcherProps {
  isSupporter?: boolean;
}

export function ThemeSwitcher({ isSupporter = false }: ThemeSwitcherProps) {
  const themeId = useThemeStore((s) => s.themeId);
  const pieceSet = useThemeStore((s) => s.pieceSet);
  const boardSet = useThemeStore((s) => s.boardSet);
  const mixMatch = useThemeStore((s) => s.mixMatch);
  const setTheme = useThemeStore((s) => s.setTheme);
  const setPieceSet = useThemeStore((s) => s.setPieceSet);
  const setBoardSet = useThemeStore((s) => s.setBoardSet);
  const setMixMatch = useThemeStore((s) => s.setMixMatch);

  return (
    <div className="space-y-5">
      <MixMatchToggle isSupporter={isSupporter} on={mixMatch} setOn={setMixMatch} />

      <Section title="Free light and dark themes">
        <div className="grid grid-cols-2 gap-3">
          {FREE_THEMES.map((id) => (
            <ThemeChip key={id} id={id} active={themeId === id} onPick={() => setTheme(id)} />
          ))}
        </div>
      </Section>

      <Section
        title="Supporter themes"
        hint={isSupporter ? undefined : 'Unlock by supporting on Ko-fi'}
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {SUPPORTER_THEMES.map((id) => (
            <ThemeChip
              key={id}
              id={id}
              active={themeId === id}
              onPick={() => {
                if (isSupporter) setTheme(id);
              }}
              locked={!isSupporter}
            />
          ))}
        </div>
      </Section>

      {mixMatch && isSupporter && (
        <Section title="Board">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {BOARD_SET_IDS.map((id) => (
              <BoardChip
                key={id}
                id={id}
                active={boardSet === id}
                onPick={() => setBoardSet(boardSet === id ? null : id)}
              />
            ))}
          </div>
          <p className="mt-2 text-xs text-ink-muted">
            Pick a board, or click the active one again to follow your theme.
          </p>
        </Section>
      )}

      <Section
        title="Piece set"
        hint={isSupporter ? undefined : 'Free: pick any Lichess set. Others unlock with support.'}
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {PIECE_SETS.map((set) => {
            const locked = set.kind === 'supporter' && !isSupporter;
            return (
              <PieceSetChip
                key={set.id}
                meta={set}
                active={pieceSet === set.id}
                locked={locked}
                onPick={() => {
                  if (!locked) setPieceSet(set.id);
                }}
              />
            );
          })}
        </div>
      </Section>
    </div>
  );
}

function MixMatchToggle({
  isSupporter,
  on,
  setOn,
}: {
  isSupporter: boolean;
  on: boolean;
  setOn: (on: boolean) => void;
}) {
  const disabled = !isSupporter;
  return (
    <div className="flex items-start justify-between gap-3 rounded-card border border-ink/10 bg-surface p-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-ink">Mix and match</span>
          {disabled && (
            <span className="text-[10px] font-semibold tracking-wide text-accent uppercase">
              Supporter
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-ink-muted">
          {isSupporter
            ? 'Pick a board and piece set independent of your theme.'
            : 'Supporters can combine any theme, board, and piece set.'}
        </p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-disabled={disabled || undefined}
        disabled={disabled}
        onClick={() => !disabled && setOn(!on)}
        className={cn(
          'shrink-0 mt-1 h-6 w-11 rounded-full border transition focus-ring',
          on ? 'bg-accent border-accent' : 'bg-ink/10 border-ink/10',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'block h-5 w-5 rounded-full bg-surface shadow transition transform',
            on ? 'translate-x-5' : 'translate-x-0.5',
          )}
        />
      </button>
    </div>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2 gap-2">
        <h3 className="text-sm font-semibold tracking-wide text-ink-muted uppercase">{title}</h3>
        {hint && <span className="text-xs text-ink-muted text-right">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function ThemeChip({
  id,
  active,
  onPick,
  locked,
}: {
  id: ThemeId;
  active: boolean;
  onPick: () => void;
  locked?: boolean;
}) {
  const theme = THEMES[id];
  const swatchStyle = {
    background: `linear-gradient(135deg,
      rgb(${theme.colors.surface}) 0% 50%,
      rgb(${theme.colors.accent}) 50% 100%)`,
    borderColor: `rgb(${theme.colors.ink} / 0.15)`,
  };
  const boardStyle = {
    background: `repeating-conic-gradient(
      rgb(${theme.colors.boardLight}) 0 25%,
      rgb(${theme.colors.boardDark}) 0 50%
    )`,
    backgroundSize: '14px 14px',
    backgroundPosition: '0 0',
  };
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={active}
      aria-disabled={locked || undefined}
      className={cn(
        'group relative text-left rounded-card overflow-hidden border transition focus-ring',
        'p-3 bg-surface',
        active ? 'border-accent shadow-card-hover' : 'border-ink/10 shadow-card',
        !locked && 'hover:-translate-y-0.5 hover:shadow-card-hover',
        locked && 'opacity-60 cursor-not-allowed',
      )}
    >
      <div className="flex items-center gap-3">
        <div aria-hidden className="h-9 w-9 rounded-md border" style={swatchStyle} />
        <div aria-hidden className="h-9 w-9 rounded-md border border-ink/10" style={boardStyle} />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-ink truncate">{theme.name}</div>
          <div className="text-xs text-ink-muted truncate">{theme.blurb}</div>
        </div>
      </div>
      {locked && (
        <div className="absolute top-2 right-2 text-[10px] font-semibold tracking-wide text-accent uppercase">
          Supporter
        </div>
      )}
      {active && (
        <div className="absolute bottom-2 right-2 text-[10px] font-semibold tracking-wide text-accent uppercase">
          Active
        </div>
      )}
    </button>
  );
}

function BoardChip({
  id,
  active,
  onPick,
}: {
  id: BoardSetId;
  active: boolean;
  onPick: () => void;
}) {
  const board = BOARD_SETS[id];
  const boardStyle = {
    background: `repeating-conic-gradient(
      rgb(${board.light}) 0 25%,
      rgb(${board.dark}) 0 50%
    )`,
    backgroundSize: '14px 14px',
    backgroundPosition: '0 0',
  };
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={active}
      className={cn(
        'group relative text-left rounded-card overflow-hidden border transition focus-ring',
        'p-3 bg-surface',
        active ? 'border-accent shadow-card-hover' : 'border-ink/10 shadow-card',
        'hover:-translate-y-0.5 hover:shadow-card-hover',
      )}
    >
      <div className="flex items-center gap-3">
        <div aria-hidden className="h-10 w-10 rounded-md border border-ink/10 shrink-0" style={boardStyle} />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-ink truncate">{board.name}</div>
          <div className="text-xs text-ink-muted truncate">{board.blurb}</div>
        </div>
      </div>
      {active && (
        <div className="absolute bottom-2 right-2 text-[10px] font-semibold tracking-wide text-accent uppercase">
          Active
        </div>
      )}
    </button>
  );
}

function PieceSetChip({
  meta,
  active,
  locked,
  onPick,
}: {
  meta: PieceSetMeta;
  active: boolean;
  locked: boolean;
  onPick: () => void;
}) {
  const previewSrc = meta.pngMono
    ? `/pieces/${meta.id}/N.png`
    : meta.inline
    ? null
    : `/pieces/${meta.id}/wN.svg`;
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={active}
      aria-disabled={locked || undefined}
      className={cn(
        'group relative text-left rounded-card overflow-hidden border transition focus-ring',
        'p-3 bg-surface',
        active ? 'border-accent shadow-card-hover' : 'border-ink/10 shadow-card',
        !locked && 'hover:-translate-y-0.5 hover:shadow-card-hover',
        locked && 'opacity-60 cursor-not-allowed',
      )}
    >
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 grid place-items-center shrink-0">
          {previewSrc ? (
            <img
              src={previewSrc}
              alt=""
              width={36}
              height={36}
              aria-hidden
              draggable={false}
              style={{ maxHeight: 36, maxWidth: 36 }}
            />
          ) : meta.inline && isInlineSet(meta.id) ? (
            <div style={{ width: 36, height: 36 }} aria-hidden>
              <InlinePiece setId={meta.id as InlinePieceSetId} type="knight" color="white" />
            </div>
          ) : null}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-ink truncate">{meta.name}</div>
          <div className="text-xs text-ink-muted truncate">{meta.blurb}</div>
        </div>
      </div>
      {locked && (
        <div className="absolute top-2 right-2 text-[10px] font-semibold tracking-wide text-accent uppercase">
          Supporter
        </div>
      )}
      {active && (
        <div className="absolute bottom-2 right-2 text-[10px] font-semibold tracking-wide text-accent uppercase">
          Active
        </div>
      )}
    </button>
  );
}

export type { PieceSetMeta };
